import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Button, Table, Badge, Spinner, Modal, Accordion, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { playNotificationSound } from '../../../shared/utils/tradeNotifications';
import { formatToLocalTime } from '../../../shared/utils/dateUtils';

const CONDITION_OPTIONS = [
  { value: 'PRICE_BELOW', label: 'Price Below' },
  { value: 'PRICE_ABOVE', label: 'Price Above' },
  { value: 'PCT_CHANGE_ABOVE', label: '% Change Above' },
  { value: 'PCT_CHANGE_BELOW', label: '% Change Below' }
];

const WINDOW_OPTIONS = [
  { value: 'day', label: 'Same Day' },
  { value: 'week', label: '1 Week' },
  { value: 'month', label: '1 Month' }
];

const LOGIC_OPTIONS = [
  { value: 'AND', label: 'AND' },
  { value: 'OR', label: 'OR' }
];

const SEVERITY_OPTIONS = [
  { value: 'LOW', label: 'Low', color: 'secondary' },
  { value: 'HIGH', label: 'High', color: 'warning' },
  { value: 'CRITICAL', label: 'Critical', color: 'danger' }
];

const SOUND_OPTIONS = [
  { value: 'soft', label: 'Soft' },
  { value: 'chime', label: 'Chime' },
  { value: 'alarm', label: 'Alarm' },
  { value: 'bell', label: 'Bell' },
  { value: 'ping', label: 'Ping' },
  { value: 'pulse', label: 'Pulse' },
  { value: 'drop', label: 'Drop' },
  { value: 'thump', label: 'Thump' },
  { value: 'glass', label: 'Glass' },
  { value: 'digital', label: 'Digital' },
  { value: 'siren', label: 'Siren' },
  { value: 'buzz', label: 'Buzz' },
  { value: 'pop', label: 'Pop' },
  { value: 'chirp', label: 'Chirp' }
];



const StockAlertRules = ({ profileId, token, isActive }) => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [watchlistSymbols, setWatchlistSymbols] = useState([]);
  const [soundSettings, setSoundSettings] = useState({
    LOW: 'soft',
    HIGH: 'chime',
    CRITICAL: 'alarm'
  });

  const [ruleName, setRuleName] = useState('');
  const [logicOperator, setLogicOperator] = useState('AND');
  const [severity, setSeverity] = useState('LOW');
  const [expiresAt, setExpiresAt] = useState('');
  const [selectedSymbols, setSelectedSymbols] = useState([]);
  const [conditions, setConditions] = useState([
    { condition_type: 'PRICE_BELOW', threshold_value: '', time_window: 'day' }
  ]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [editRuleName, setEditRuleName] = useState('');
  const [editLogicOperator, setEditLogicOperator] = useState('AND');
  const [editSeverity, setEditSeverity] = useState('LOW');
  const [editExpiresAt, setEditExpiresAt] = useState('');
  const [editSymbols, setEditSymbols] = useState([]);
  const [editConditions, setEditConditions] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchRules = async () => {
    if (!profileId || !token) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/alerts/profiles/${profileId}`, { headers });
      if (!response.ok) throw new Error('Failed to load alert rules');
      const data = await response.json();
      setRules(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchWatchlistSymbols = async () => {
    if (!profileId || !token) return;
    try {
      const watchlistsResponse = await fetch(
        `${API_BASE}/api/stock-market/profiles/${profileId}/watchlists`,
        { headers }
      );
      if (!watchlistsResponse.ok) return;
      const watchlists = await watchlistsResponse.json();
      const allSymbols = new Set();

      for (const watchlist of watchlists) {
        const stocksResponse = await fetch(
          `${API_BASE}/api/stock-market/watchlists/${watchlist.id}/stocks`,
          { headers }
        );
        if (!stocksResponse.ok) continue;
        const items = await stocksResponse.json();
        items.forEach(item => {
          const symbol = item.stock?.ticker_symbol || item.ticker_symbol;
          if (symbol) allSymbols.add(symbol);
        });
      }

      if (allSymbols.size === 0) {
        const legacyResponse = await fetch(
          `${API_BASE}/api/stock-market/profiles/${profileId}/watchlist`,
          { headers }
        );
        if (legacyResponse.ok) {
          const legacyItems = await legacyResponse.json();
          legacyItems.forEach(item => {
            const symbol = item.stock?.ticker_symbol || item.ticker_symbol;
            if (symbol) allSymbols.add(symbol);
          });
        }
      }

      setWatchlistSymbols(Array.from(allSymbols).sort());
    } catch (error) {
      console.error('Failed to fetch watchlist symbols:', error);
    }
  };

  useEffect(() => {
    fetchRules();
    fetchWatchlistSymbols();
    try {
      const raw = localStorage.getItem('alert_sound_settings');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed) {
          setSoundSettings(prev => ({ ...prev, ...parsed }));
        }
      }
    } catch {
      // ignore localStorage errors
    }
  }, [profileId, token]);

  useEffect(() => {
    if (!isActive) return;
    fetchRules();
    const intervalId = setInterval(() => {
      fetchRules();
    }, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [isActive, profileId, token]);

  const handleSoundChange = (level, value) => {
    setSoundSettings(prev => {
      const next = { ...prev, [level]: value };
      try {
        localStorage.setItem('alert_sound_settings', JSON.stringify(next));
      } catch {
        // ignore localStorage errors
      }
      return next;
    });
  };

  const addSymbol = (symbol) => {
    const normalized = symbol.trim().toUpperCase();
    if (!normalized) return;
    setSelectedSymbols(prev => prev.includes(normalized) ? prev : [...prev, normalized]);
  };

  const removeSymbol = (symbol) => {
    setSelectedSymbols(prev => prev.filter(item => item !== symbol));
  };

  const updateCondition = (index, field, value) => {
    setConditions(prev => prev.map((condition, idx) => (
      idx === index ? { ...condition, [field]: value } : condition
    )));
  };

  const addCondition = () => {
    setConditions(prev => [...prev, { condition_type: 'PRICE_BELOW', threshold_value: '', time_window: 'day' }]);
  };

  const removeCondition = (index) => {
    setConditions(prev => prev.filter((_, idx) => idx !== index));
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setEditRuleName(rule.name || '');
    setEditLogicOperator(rule.logic_operator || 'AND');
    setEditSeverity(rule.severity || 'LOW');
    setEditSymbols(rule.symbols ? [...rule.symbols] : []);
    setEditConditions(rule.conditions ? rule.conditions.map(condition => ({
      ...condition,
      threshold_value: condition.threshold_value?.toString() || ''
    })) : []);
    setEditExpiresAt(rule.expires_at ? new Date(rule.expires_at).toISOString().slice(0, 16) : '');
    setShowEditModal(true);
  };

  const closeEditRule = () => {
    if (savingEdit) return;
    setShowEditModal(false);
    setEditingRule(null);
  };

  const addEditSymbol = (symbol) => {
    const normalized = symbol.trim().toUpperCase();
    if (!normalized) return;
    setEditSymbols(prev => prev.includes(normalized) ? prev : [...prev, normalized]);
  };

  const removeEditSymbol = (symbol) => {
    setEditSymbols(prev => prev.filter(item => item !== symbol));
  };

  const updateEditCondition = (index, field, value) => {
    setEditConditions(prev => prev.map((condition, idx) => (
      idx === index ? { ...condition, [field]: value } : condition
    )));
  };

  const addEditCondition = () => {
    setEditConditions(prev => [...prev, { condition_type: 'PRICE_BELOW', threshold_value: '', time_window: 'day' }]);
  };

  const removeEditCondition = (index) => {
    setEditConditions(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleUpdateRule = async () => {
    if (!editingRule) return;
    if (editSymbols.length === 0) {
      toast.error('Select at least one symbol');
      return;
    }
    const sanitizedConditions = editConditions.map(condition => ({
      ...condition,
      threshold_value: parseFloat(condition.threshold_value)
    }));
    if (sanitizedConditions.some(condition => !Number.isFinite(condition.threshold_value) || condition.threshold_value <= 0)) {
      toast.error('All thresholds must be positive numbers');
      return;
    }

    try {
      setSavingEdit(true);
      const response = await fetch(`${API_BASE}/api/alerts/profiles/${profileId}/${editingRule.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          name: editRuleName || null,
          symbols: editSymbols,
          logic_operator: editLogicOperator,
          severity: editSeverity,
          expires_at: editExpiresAt ? new Date(editExpiresAt).toISOString() : null,
          conditions: sanitizedConditions
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update rule');
      toast.success('Alert rule updated');
      setRules(prev => prev.map(item => (item.id === data.id ? data : item)));
      setShowEditModal(false);
      setEditingRule(null);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCreateRule = async () => {
    if (selectedSymbols.length === 0) {
      toast.error('Select at least one symbol');
      return;
    }
    const sanitizedConditions = conditions.map(condition => ({
      ...condition,
      threshold_value: parseFloat(condition.threshold_value)
    }));
    if (sanitizedConditions.some(condition => !Number.isFinite(condition.threshold_value) || condition.threshold_value <= 0)) {
      toast.error('All thresholds must be positive numbers');
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${API_BASE}/api/alerts/profiles/${profileId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: ruleName || null,
          symbols: selectedSymbols,
          logic_operator: logicOperator,
          severity,
          expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
          is_active: true,
          conditions: sanitizedConditions
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to create rule');
      toast.success('Alert rule created');
      setRuleName('');
      setSelectedSymbols([]);
      setConditions([{ condition_type: 'PRICE_BELOW', threshold_value: '', time_window: 'day' }]);
      setSeverity('LOW');
      setExpiresAt('');
      setRules(prev => [data, ...prev]);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      const response = await fetch(`${API_BASE}/api/alerts/profiles/${profileId}/${rule.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ is_active: !rule.is_active })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update rule');
      setRules(prev => prev.map(item => (item.id === data.id ? data : item)));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Delete this alert rule?')) return;
    try {
      const response = await fetch(`${API_BASE}/api/alerts/profiles/${profileId}/${ruleId}`, {
        method: 'DELETE',
        headers
      });
      if (!response.ok) throw new Error('Failed to delete rule');
      setRules(prev => prev.filter(item => item.id !== ruleId));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const renderStatusBadge = (rule) => {
    if (rule.expires_at && new Date(rule.expires_at) <= new Date()) {
      return <Badge bg="dark">Expired</Badge>;
    }
    if (!rule.is_active) {
      return <Badge bg="secondary">Paused</Badge>;
    }
    if (rule.last_condition_met === true) {
      return <Badge bg="success">Triggered</Badge>;
    }
    if (rule.last_condition_met === false) {
      return <Badge bg="warning" text="dark">Watching</Badge>;
    }
    return <Badge bg="light" text="dark">Pending</Badge>;
  };

  const renderSeverityBadge = (severityValue) => {
    const match = SEVERITY_OPTIONS.find(option => option.value === severityValue);
    return <Badge bg={match?.color || 'secondary'}>{severityValue}</Badge>;
  };

  const formatConditionSummary = (rule) => {
    if (!rule.conditions) return '-';
    return rule.conditions.map(condition => (
      `${condition.condition_type.replace(/_/g, ' ')} ${condition.threshold_value}${condition.condition_type.includes('PCT_CHANGE') ? '%' : ''} (${condition.time_window})`
    )).join(` ${rule.logic_operator} `);
  };

  const formatExecutionTooltip = (rule) => {
    if (rule.last_execution_details) {
      return rule.last_execution_details;
    }
    if (rule.expires_at && new Date(rule.expires_at) <= new Date()) {
      return 'Rule expired and is no longer evaluated.';
    }
    if (!rule.last_evaluated_at) {
      return 'Not evaluated yet.';
    }
    if (rule.last_condition_met === true) {
      return 'Rule conditions were met in the last evaluation.';
    }
    if (rule.last_condition_met === false) {
      return 'Rule conditions were not met in the last evaluation.';
    }
    return 'Awaiting evaluation.';
  };

  const dateTimeRenderOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  };

  return (
    <div>
      <Card className="mb-4">
        <Card.Header className="bg-white d-flex align-items-center justify-content-between">
          <strong>Alert Rules</strong>
          <Button variant="outline-secondary" size="sm" onClick={fetchRules}>
            Refresh
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Symbols</th>
                  <th>Conditions</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Last Checked</th>
                  <th>Last Triggered</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      No alert rules yet.
                    </td>
                  </tr>
                ) : (
                  rules.map(rule => (
                    <tr key={rule.id}>
                      <td>{rule.name || '-'}</td>
                      <td>
                        {rule.symbols?.map(symbol => (
                          <Badge key={symbol} bg="light" text="dark" className="border me-1">{symbol}</Badge>
                        ))}
                      </td>
                      <td>{formatConditionSummary(rule)}</td>
                      <td>{renderSeverityBadge(rule.severity)}</td>
                      <td>
                        <OverlayTrigger
                          placement="top"
                          overlay={(
                            <Tooltip id={`rule-${rule.id}-status`}>
                              {formatExecutionTooltip(rule)}
                            </Tooltip>
                          )}
                        >
                          <span className="d-inline-block">
                            {renderStatusBadge(rule)}
                          </span>
                        </OverlayTrigger>
                      </td>
                      <td>{formatToLocalTime(rule.last_evaluated_at, dateTimeRenderOptions)}</td>
                      <td>{formatToLocalTime(rule.last_triggered_at, dateTimeRenderOptions)}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => openEditRule(rule)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant={rule.is_active ? 'outline-secondary' : 'outline-success'}
                            onClick={() => handleToggleRule(rule)}
                          >
                            {rule.is_active ? 'Pause' : 'Resume'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDeleteRule(rule.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Accordion defaultActiveKey="create" className="mb-4">
        <Accordion.Item eventKey="create">
          <Accordion.Header>Create Alert Rule</Accordion.Header>
          <Accordion.Body>
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Rule Name (Optional)</Form.Label>
                      <Form.Control
                        value={ruleName}
                        onChange={(e) => setRuleName(e.target.value)}
                        placeholder="e.g. Breakout Alerts"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Logic</Form.Label>
                      <Form.Select
                        value={logicOperator}
                        onChange={(e) => setLogicOperator(e.target.value)}
                      >
                        {LOGIC_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Severity</Form.Label>
                      <Form.Select
                        value={severity}
                        onChange={(e) => setSeverity(e.target.value)}
                      >
                        {SEVERITY_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Expires At (Optional)</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Add Symbol</Form.Label>
                      <Form.Select
                        value=""
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value) addSymbol(value);
                        }}
                      >
                        <option value="">Select from Watchlist</option>
                        {watchlistSymbols.map(symbol => (
                          <option key={symbol} value={symbol}>{symbol}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {selectedSymbols.length > 0 && (
                  <div className="mt-3 d-flex flex-wrap gap-2">
                    {selectedSymbols.map(symbol => (
                      <Badge key={symbol} bg="light" text="dark" className="border">
                        {symbol}
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger ms-1 p-0"
                          onClick={() => removeSymbol(symbol)}
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="mt-4">
                  <strong>Conditions</strong>
                  {conditions.map((condition, index) => (
                    <Row className="g-2 align-items-end mt-2" key={`condition-${index}`}>
                      <Col md={5}>
                        <Form.Group>
                          <Form.Label>Condition</Form.Label>
                          <Form.Select
                            value={condition.condition_type}
                            onChange={(e) => updateCondition(index, 'condition_type', e.target.value)}
                          >
                            {CONDITION_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Threshold</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            value={condition.threshold_value}
                            onChange={(e) => updateCondition(index, 'threshold_value', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Window</Form.Label>
                          <Form.Select
                            value={condition.time_window}
                            onChange={(e) => updateCondition(index, 'time_window', e.target.value)}
                          >
                            {WINDOW_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={1}>
                        <Button
                          variant="outline-danger"
                          onClick={() => removeCondition(index)}
                          disabled={conditions.length === 1}
                        >
                          ×
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Button variant="outline-secondary" size="sm" className="mt-3" onClick={addCondition}>
                    Add Condition
                  </Button>
                </div>

                <div className="mt-4 d-grid">
                  <Button
                    variant="primary"
                    onClick={handleCreateRule}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Create Rule'}
                  </Button>
                </div>
                <div className="text-muted small mt-3">
                  Alerts trigger per symbol based on the latest market price and selected windows.
                </div>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="sounds">
              <Accordion.Header>Notification Sounds</Accordion.Header>
              <Accordion.Body>
                <Row className="g-3">
                  {['LOW', 'HIGH', 'CRITICAL'].map(level => (
                    <Col md={12} key={level}>
                      <Form.Group>
                        <Form.Label>{level.charAt(0) + level.slice(1).toLowerCase()}</Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Select
                            value={soundSettings[level]}
                            onChange={(e) => handleSoundChange(level, e.target.value)}
                          >
                            {SOUND_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </Form.Select>
                          <Button
                            variant="outline-secondary"
                            onClick={() => playNotificationSound(level)}
                          >
                            Test
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
                <div className="text-muted small mt-3">
                  Sounds are stored locally in this browser.
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

      <Modal show={showEditModal} onHide={closeEditRule} size="lg" centered>
        <Modal.Header closeButton={!savingEdit}>
          <Modal.Title>Edit Alert Rule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Rule Name (Optional)</Form.Label>
                <Form.Control
                  value={editRuleName}
                  onChange={(e) => setEditRuleName(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Logic</Form.Label>
                <Form.Select
                  value={editLogicOperator}
                  onChange={(e) => setEditLogicOperator(e.target.value)}
                >
                  {LOGIC_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Severity</Form.Label>
                <Form.Select
                  value={editSeverity}
                  onChange={(e) => setEditSeverity(e.target.value)}
                >
                  {SEVERITY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="g-3 mt-2">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Expires At (Optional)</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={editExpiresAt}
                  onChange={(e) => setEditExpiresAt(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="mt-3">
            <Form.Label>Add Symbol</Form.Label>
            <div className="d-flex gap-2">
              <Form.Select
                value=""
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) addEditSymbol(value);
                }}
              >
                <option value="">Select from Watchlist</option>
                {watchlistSymbols.map(symbol => (
                  <option key={symbol} value={symbol}>{symbol}</option>
                ))}
              </Form.Select>
            </div>
          </div>

          {editSymbols.length > 0 && (
            <div className="mt-3 d-flex flex-wrap gap-2">
              {editSymbols.map(symbol => (
                <Badge key={symbol} bg="light" text="dark" className="border">
                  {symbol}
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger ms-1 p-0"
                    onClick={() => removeEditSymbol(symbol)}
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="mt-4">
            <strong>Conditions</strong>
            {editConditions.map((condition, index) => (
              <Row className="g-2 align-items-end mt-2" key={`edit-condition-${index}`}>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Condition</Form.Label>
                    <Form.Select
                      value={condition.condition_type}
                      onChange={(e) => updateEditCondition(index, 'condition_type', e.target.value)}
                    >
                      {CONDITION_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Threshold</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={condition.threshold_value}
                      onChange={(e) => updateEditCondition(index, 'threshold_value', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Window</Form.Label>
                    <Form.Select
                      value={condition.time_window}
                      onChange={(e) => updateEditCondition(index, 'time_window', e.target.value)}
                    >
                      {WINDOW_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Button
                    variant="outline-danger"
                    onClick={() => removeEditCondition(index)}
                    disabled={editConditions.length === 1}
                    className="w-100"
                  >
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}
            <Button variant="outline-secondary" size="sm" className="mt-3" onClick={addEditCondition}>
              Add Condition
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditRule} disabled={savingEdit}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateRule} disabled={savingEdit}>
            {savingEdit ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StockAlertRules;
